import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, Subject } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { IAuthenticateResponse } from '../models/user/authenticateResponse.interface';
import { LoaderService } from 'src/app/services/loader.service';
import { NotificationService } from 'src/app/services/notification.service';
import { IAffiliate } from '../models/user/affiliate.interface';
import { AffiliateRole } from '../models/util/tmEnums';
import { AuthentificationProviderService } from './providers/authentification-provider.service';
import { DeployParams } from 'src/app/deployParams';
import { StorageService } from 'src/app/services/util/storage.service';
import { ErrorLevel, ErrorType } from 'src/app/models/enums';
import { ICustomNotification } from 'src/app/models/notification/customNotification.interface';
import { DynamicTranslations } from 'src/locale/dynamicTranslations';
import { ModelMapperTMService } from './model-mapper-tm.service';
import { ShopProviderService } from './providers/shop.provider.service';
import { environment } from 'src/environments/environment';



@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private loggedUser: IAffiliate;

  private loggedAffiliateChangedSource = new Subject<void>();
  public loggedAffiliateChangedSource$ = this.loggedAffiliateChangedSource.asObservable();


  constructor(
    private router: Router,
    private authentificationProvider: AuthentificationProviderService,
    private storageService: StorageService,
    private notificationService: NotificationService,
    private loaderService: LoaderService,
    private modelMapperTM: ModelMapperTMService,
    private shopProvider: ShopProviderService
  ) {
    if (this.storageService.affiliate){
      this.loggedUser = this.storageService.affiliate;
    } else {
      // this.router.navigate(['/betting/prematch']);
    }
  }

  get loggedAffiliate(){
    return this.loggedUser;
  }

  updateUserData(affiliate: IAffiliate){
    this.loggedUser.firstName = affiliate.firstName;
    this.loggedUser.lastName = affiliate.lastName;
    this.loggedUser.identityNumber = affiliate.identityNumber;
    this.loggedUser.email = affiliate.email;
    this.loggedUser.address = affiliate.address;
    this.loggedUser.city = affiliate.city;
    this.loggedUser.state = affiliate.state;
    this.loggedUser.phone = affiliate.phone;
    this.loggedUser.gender = affiliate.gender;
    this.loggedUser.birthDate = affiliate.birthDate;
    this.storageService.affiliate = this.loggedUser;
  }

  login(username: string, password: string): Observable<{sucsses: boolean; errMsg: string}> {
    return this.authentificationProvider.login(username, password).pipe(
      switchMap((authResp) => {
        if (authResp.resp) {
          this.doLoginUser(authResp.resp);
          return of({sucsses: true, errMsg: authResp.err});
        } else {
          return of({sucsses: false, errMsg: authResp.err});
        }
      }));
  }

  checkConfirmToken(token: string): Observable<any> {
    return this.authentificationProvider.checkConfirmToken(token);
    // const params = new HttpParams();
    // return this.http.get<IBaseResponse>(`${this.tcketManagerBaseUrl}affiliate/checkRegistrationToken/${token}`)
    // .pipe(
    //   map((response) => response.data.isTokenValid as boolean),
    //   publishReplay(1),
    //   refCount(),
    //   catchError((err) => {logout
    //     console.log(err);
    //     return [];
    //   }),
    // );
  }

  logout(isManual: boolean = false) {
    this.loaderService.showHideLoader(false);
    if (isManual) {
      this.authentificationProvider.logout(this.storageService.affiliateRefreshToken).pipe(tap(() => this.doLogoutUser())).subscribe();
    } else {
      this.doLogoutUser();
    }
  }

  isLoggedIn() {
    return !!this.storageService.affiliateJwtToken;
  }

  refreshToken(): Observable<IAuthenticateResponse> {
    return this.authentificationProvider.refreshToken(this.storageService.affiliateRefreshToken).pipe(
      switchMap((authResponse) => {
      if (authResponse) {
        this.storeTokens(authResponse.jwtToken, authResponse.refreshToken);
        return of(authResponse);
      } else {
        const notification = {
          title: DynamicTranslations.notification_title,
          message: DynamicTranslations.notification_LoggedOutAuto_ErrorMessage,
          errorLevel: ErrorLevel.Notification,
          errorType: ErrorType.Warning
        } as ICustomNotification;
        this.notificationService.forwardDataToNotification(notification);
        this.logout();
        return of(null);
      }
    }));
    //  const httpHeadersWithRefreshToken = new HttpHeaders({
    //   // eslint-disable-next-line @typescript-eslint/naming-convention
    //   'Content-Type': 'application/json',
    //   refreshToken: this.refreshTokenStorage
    // });
    //  return this.http.post<IBaseResponse>(`${this.tcketManagerBaseUrl}affiliate/refresh-token`, {},
    //   { headers: httpHeadersWithRefreshToken }).pipe(tap((response: IBaseResponse) => {
    //     this.storeTokens(response.data.jwtToken, response.data.refreshToken);
    // }));
  }

  // getJwtToken() {
  //   return localStorage.getItem(this.JWT_TOKEN);
  // }

  private doLoginUser(_response: IAuthenticateResponse) {
    this.storeTokens(_response.jwtToken, _response.refreshToken);
    this.loggedUser = this.modelMapperTM.mapAffiliate(_response.affiliate, _response.parentName);
    this.storageService.accountBalance = this.loggedUser.currentBalance;
    this.storageService.freeBetBalance = null;
    this.loggedUser.affiliateRole = this.getRoleFromJwtToken();
    this.storageService.affiliate = this.loggedUser;
    if (this.loggedUser.affiliateRole === AffiliateRole.cashier) {
      this.router.navigate(['/account/cashdesk']);
      if (this.loggedUser.workInShopId) {
        this.shopProvider.getShop(this.loggedUser.workInShopId).subscribe(s => {
          this.loggedUser.worksInShop = s;
          this.storageService.affiliate = this.loggedUser;
        });
      }
    } else {
      this.router.navigate(['/account/statement']);
    }
    this.loggedAffiliateChangedSource.next();
  }

  getRoleFromJwtToken(): AffiliateRole{
    const jwtData = this.storageService.affiliateJwtToken.split('.')[1];
    const decodedJwtJsonData = window.atob(jwtData);
    const decodedJwtData = JSON.parse(decodedJwtJsonData);
    // console.log(this.returnTokenEnumFromString(decodedJwtData.role), ' ROLA');
    return this.returnTokenEnumFromString(decodedJwtData.role);
  }

  getAffiliateIdFromJwt(): number{
    const jwtData = this.storageService.affiliateJwtToken.split('.')[1];
    const decodedJwtJsonData = window.atob(jwtData);
    const decodedJwtData = JSON.parse(decodedJwtJsonData);
    return +decodedJwtData.AffiliateId;
  }

  private returnTokenEnumFromString(role: string) {
    switch (role){
      case  'Cashier' : return AffiliateRole.cashier;
      case  'Agent' : return AffiliateRole.agent;
      case  'SuperAgent' : return AffiliateRole.superAgent;
      case  'AgentManager' : return AffiliateRole.agentManager;
      case  'MasterAgent' : return AffiliateRole.masterAgent;
      case  'OperatorTrader' : return AffiliateRole.operatorTrader;
    }
  }

  private doLogoutUser() {

    // this.loggedUser = null;
    // this.storageService.affiliateRefreshToken = null;
    // this.storageService.affiliateJwtToken = null;
    // this.storageService.affiliate = null;
    this.storageService.accountBalance = null;
    this.storageService.freeBetBalance = null;
    this.removeTokensAndUser();
    this.router.navigate(['/betting/prematch']);
    this.loggedAffiliateChangedSource.next();
  }

  removeTokensAndUser(){
    this.loggedUser = null;
    this.storageService.affiliateRefreshToken = null;
    this.storageService.affiliateJwtToken = null;
    this.storageService.affiliate = null;
  }
  // private getRefreshToken() {
  //   return localStorage.getItem('refreshToken');
  // }


  private storeTokens(jwt: string, refreshToken: string) {
    this.storageService.affiliateJwtToken = jwt;
    this.storageService.affiliateRefreshToken = refreshToken;
  }

  get officeId(){
    return this.loggedUser && this.loggedUser.workInShopId ? this.loggedUser.workInShopId : environment.betWebOfficeId;
  }

  changebalance(balance: number) {
    this.loggedUser.currentBalance = balance;
    this.storageService.affiliate = this.loggedUser;
    this.storageService.accountBalance = balance;
  }


  // setOfficeForUser(loggedOffice: IOfficeDescription) {
  //   if (this.userTM) {
  //     this.userTM.loggedOffice = loggedOffice;
  //     this.storage.userTM = this.userTM;
  //    // localStorage.setItem('tmUser', JSON.stringify(this.userTM));
  //     this.tmUserChangedSource.next();
  //     this.settingsService.reloadSettings();
  //   }
  // }
}
